import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { PageCreate, PageCreateConstructor } from "../../models/PageCreate";
import { useCreatePage } from "../../hooks/useCreatePage";
import { useFieldRules } from "../../../../hooks/useFieldRules";
import { useApiFieldErrors } from "../../../../hooks/fetch/useApiFieldErrors";

import DomainSelectForm from "../../../domain/components/form/DomainSelectForm";
import CategorySelectForm from "../../../categories/components/CategorySelectForm";
import EntitySettingsForm from "../../../setting/components/EntitySettingsForm";

import FormCardWrapper from "../../../../components/reusable-components/form/FormCardWrapper";
import FormTextField from "../../../../components/reusable-components/form/MuiFormInput";
import RichTextForm from "../../../../components/reusable-components/form/RichTextForm";
import SubmitButton from "../../../../components/reusable-components/form/SubmitButton";

import { EntityTypeLink } from "../../../setting/models/enums/EntityTypeLink";
import Grid from "@mui/material/Grid";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Typography,
} from "@mui/material";
import FormFileInput from "../../../../components/reusable-components/form/FormFileInput";
import { useEffect, useMemo } from "react";

const CreatePage = () => {
  const navigate = useNavigate();
  const rules = useFieldRules();

  const {
    fetch: {
      fetchData: createPage,
      loading: creating,
      error: createError,
      success: createSuccess,
    },
  } = useCreatePage();

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm<PageCreateConstructor>({
    defaultValues: {
      title: "",
      slug: "",
      body: "",
      domain_id: undefined,
      category_id: undefined,
      settings: [],
      featured_image: null,
      featured_image_file: null,
    },
    mode: "all",
  });

  useApiFieldErrors(createError, setError);

  const onSubmit: SubmitHandler<PageCreateConstructor> = async (formData) => {
    clearErrors();

    console.log("Form Data:", formData);

    const pageCreate = new PageCreate(formData).toFormData();
    const result = await createPage({ body: pageCreate });

    if (result?.id) {
      navigate(`/pages/edit/${result.id}`);
    }
  };

  const isLoading = creating || isSubmitting;

  const featuredImage = watch("featured_image");
  const featuredImageFile = watch("featured_image_file");

  useEffect(() => {
    if (featuredImage && featuredImage.trim() !== "") {
      setValue("featured_image_file", null);
    }
  }, [featuredImage]);

  useEffect(() => {
    if (featuredImageFile && featuredImageFile.length > 0) {
      setValue("featured_image", "");
    }
  }, [featuredImageFile]);

  const featuredImageFilePreview = useMemo(() => {
    return featuredImageFile?.[0]
      ? URL.createObjectURL(featuredImageFile[0])
      : null;
  }, [featuredImageFile]);

  return (
    <FormCardWrapper
      title="Create Page"
      successText="Page created successfully!"
      success={createSuccess}
      error={createError}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="title"
            label="Title"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Title").required().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="slug"
            label="Slug"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Slug").required().slug().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DomainSelectForm
            name="domain_id"
            control={control}
            disabled={isLoading}
            controllerProps={{
              rules: rules("Domain").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CategorySelectForm
            name="category_id"
            control={control}
            setValue={setValue}
            watch={watch}
            domainId={watch("domain_id")}
            disabled={isLoading || !watch("domain_id")?.value}
            controllerProps={{
              rules: rules("Category").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Featured Image
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You can only provide either a featured image link or upload an
                image file. Not both.
              </Typography>

              <Grid container spacing={2} mt={1}>
                <Grid size={12}>
                  <FormTextField
                    name="featured_image"
                    label="Featured Image LINK"
                    control={control}
                    textFieldProps={{
                      disabled:
                        isLoading || (featuredImageFile?.length ?? 0) > 0,
                    }}
                    rules={rules("Featured Image LINK").url().get()}
                  />
                </Grid>

                {featuredImage && (
                  <Box mt={2}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={featuredImage}
                      alt="Featured Preview"
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #ccc",
                        objectFit: "contain",
                        maxWidth: "100%",
                      }}
                    />
                  </Box>
                )}

                <Grid size={12}>
                  <FormFileInput
                    name="featured_image_file"
                    buttonProps={{ fullWidth: true }}
                    control={control}
                    disabled={
                      isLoading || (featuredImage?.trim() ?? "").length > 0
                    }
                    label="Featured Image Upload"
                    rules={rules("Featured Image")
                      .file(["jpg", "jpeg", "png", "webp"])
                      .get()}
                  />
                </Grid>
                {featuredImageFilePreview && (
                  <Box mt={2}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={featuredImageFilePreview}
                      alt="Featured Preview"
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #ccc",
                        objectFit: "contain",
                        maxWidth: "100%",
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={12}>
          <RichTextForm
            name="body"
            label="Body"
            control={control}
            disabled={isLoading}
            rules={rules("Body").required().get()}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        <Grid size={12}>
          <EntitySettingsForm
            control={control}
            entityType={EntityTypeLink.PAGES}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={12}>
          <SubmitButton
            text="Create Page"
            loading={isLoading}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </FormCardWrapper>
  );
};

export default CreatePage;
